/**
 * Token utility functions for extracting user information from JWT token
 * This provides an additional layer of security by reading from token instead of localStorage
 */

/**
 * Decode JWT token payload (without verification)
 * Note: This is for client-side UI purposes only. Backend must always verify the token.
 */
export const decodeToken = (token: string): any | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode payload (second part)
    const payload = parts[1];
    if (!payload) {
      return null;
    }
    
    // Add padding if needed for base64 decode
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(base64));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get user role from JWT token
 * Returns null if token is invalid or role is not found
 */
export const getRoleFromToken = (token: string | null): string | null => {
  if (!token) {
    return null;
  }

  try {
    const decoded = decodeToken(token);
    if (!decoded) {
      return null;
    }

    // Try different possible role claim names
    // Common JWT claim names: role, roles, userRole, user_role, http://schemas.microsoft.com/ws/2008/06/identity/claims/role
    return decoded.role || 
           decoded.roles?.[0] || 
           decoded.userRole || 
           decoded.user_role ||
           decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
           null;
  } catch (error) {
    console.error('Error getting role from token:', error);
    return null;
  }
};

/**
 * Get user ID from JWT token
 */
export const getUserIdFromToken = (token: string | null): number | null => {
  if (!token) {
    return null;
  }

  try {
    const decoded = decodeToken(token);
    if (!decoded) {
      return null;
    }

    // Try different possible ID claim names
    return decoded.id || 
           decoded.userId || 
           decoded.user_id ||
           decoded.sub || // JWT standard subject claim
           decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
           null;
  } catch (error) {
    console.error('Error getting user ID from token:', error);
    return null;
  }
};

/**
 * Verify if token is expired
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }

    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    return currentTime >= expirationTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

